import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Checkbox,
  FileInput,
  Group,
  Loader,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconCircleCheck, IconFileSpreadsheet } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUnsavedChanges } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import {
  emptyMaterialForm,
  MATERIAL_TYPE_OPTIONS,
  MATERIAL_UNIT_OPTIONS,
  type MaterialFormValues,
  materialFormOf,
  materialFormSchema,
  suggestedFormType,
  toMaterialInput,
} from './forms.js';
import classes from './Materials.module.css';
import type { CatalogEditor } from './source.js';
import type { CatalogImportReport, MaterialCategory, MaterialDetail } from './types.js';

/** Sunucu hata kodunun iletisi; bilinmeyen kod genel hataya düşer. */
export function useErrorText() {
  const { t } = useTranslation();
  return (code: string) =>
    t(`materials.errors.${code}`, { defaultValue: t('materials.errors.GENERIC') });
}

export interface MaterialDialogProps {
  opened: boolean;
  /** `null`: yeni kalem. */
  material: MaterialDetail | null;
  categories: readonly MaterialCategory[];
  catalog: CatalogEditor;
  onClose: () => void;
  onSaved: (material: MaterialDetail) => void;
}

/** Ofiste kalem açma ve düzeltme (Material Detail alanları; ADR 0026 §4). */
export function MaterialDialog(props: MaterialDialogProps) {
  const { opened, material, categories, catalog, onClose, onSaved } = props;
  const { t } = useTranslation();
  const k = (key: string) => t(`materials.form.${key}`);
  const errorText = useErrorText();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<MaterialFormValues>({
    resolver: zodResolver(materialFormSchema),
    defaultValues: emptyMaterialForm(),
  });
  const { errors, isSubmitting, isDirty } = form.formState;
  // Kaydedilmemiş değişiklikle sayfadan çıkılırken onay istenir (M6f G5-56)
  useUnsavedChanges(opened && isDirty);

  useEffect(() => {
    if (!opened) return;
    setError(null);
    form.reset(material ? materialFormOf(material) : emptyMaterialForm());
  }, [opened, material, form]);

  // Yeni kalemde tür kategoriden ya da IMPA'dan önerilir (ADR 0122 §2); kullanıcı türü
  // kendisi seçtiyse öneri uygulanmaz
  const watchedCategory = form.watch('categoryId');
  const watchedImpa = form.watch('impaCode');
  useEffect(() => {
    if (!opened || material || form.getFieldState('materialType').isDirty) return;
    const suggested = suggestedFormType(
      { categoryId: watchedCategory, impaCode: watchedImpa },
      categories,
    );
    if (form.getValues('materialType') !== suggested) form.setValue('materialType', suggested);
  }, [opened, material, form, categories, watchedCategory, watchedImpa]);

  const categoryOptions = categories.map((c) => ({
    value: c.id,
    label: `${c.mainCategory} › ${c.subCategory}`,
  }));
  const fieldError = (key: keyof MaterialFormValues) => {
    const message = errors[key]?.message;
    return message ? t(message) : undefined;
  };

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const input = toMaterialInput(values);
      const saved = material
        ? await catalog.update(material.id, { rowVersion: material.rowVersion, ...input })
        : await catalog.create(input);
      onSaved(saved);
    } catch (failure) {
      setError(errorCode(failure));
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={material ? k('editTitle') : k('newTitle')}
      closeButtonProps={{ 'aria-label': k('close') }}
      size="lg"
    >
      <form onSubmit={submit} noValidate>
        <Stack gap="sm">
          {error ? (
            <Alert color="red" icon={<IconAlertCircle size={18} />} role="alert">
              {errorText(error)}
            </Alert>
          ) : null}
          <Controller
            control={form.control}
            name="categoryId"
            render={({ field }) => (
              <Select
                label={k('category')}
                data={categoryOptions}
                searchable
                required
                value={field.value || null}
                onChange={(value) => field.onChange(value ?? '')}
                error={fieldError('categoryId')}
              />
            )}
          />
          <Controller
            control={form.control}
            name="secondaryCategoryId"
            render={({ field }) => (
              <Select
                label={k('secondaryCategory')}
                description={k('secondaryHint')}
                data={categoryOptions}
                searchable
                clearable
                value={field.value || null}
                onChange={(value) => field.onChange(value ?? '')}
                error={fieldError('secondaryCategoryId')}
              />
            )}
          />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput
              label={k('name')}
              required
              {...form.register('name')}
              error={fieldError('name')}
            />
            <TextInput
              label={k('specification')}
              {...form.register('specification')}
              error={fieldError('specification')}
            />
            <Controller
              control={form.control}
              name="unit"
              render={({ field }) => (
                <Select
                  label={k('unit')}
                  data={[...MATERIAL_UNIT_OPTIONS]}
                  allowDeselect={false}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  error={fieldError('unit')}
                />
              )}
            />
            <Controller
              control={form.control}
              name="materialType"
              render={({ field }) => (
                <Select
                  label={k('materialType')}
                  data={MATERIAL_TYPE_OPTIONS.map((value) => ({
                    value,
                    label: t(`materials.types.${value}`),
                  }))}
                  allowDeselect={false}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  error={fieldError('materialType')}
                />
              )}
            />
            <TextInput
              label={k('companyCode')}
              {...form.register('companyCode')}
              error={fieldError('companyCode')}
            />
            <TextInput
              label={k('impaCode')}
              {...form.register('impaCode')}
              error={fieldError('impaCode')}
            />
            <TextInput
              label={k('issaCode')}
              {...form.register('issaCode')}
              error={fieldError('issaCode')}
            />
          </SimpleGrid>
          <Checkbox label={k('critical')} {...form.register('critical')} />
          <Textarea
            label={k('notes')}
            autosize
            minRows={2}
            {...form.register('notes')}
            error={fieldError('notes')}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {k('cancel')}
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {k('save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export interface ImportDialogProps {
  opened: boolean;
  catalog: CatalogEditor;
  onClose: () => void;
  onImported: (report: CatalogImportReport) => void;
}

const MAX_BYTES = 10 * 1024 * 1024;

/**
 * Excel'den içe aktarma (ADR 0026 §4): müşterinin `BGM_Material_Master_5000.xlsx` biçimi, yalnız
 * `01_ALL_ITEMS` sayfası, en çok 10 MB. Rapor: sayılar, satır hataları, uyarılar ve başka
 * kategoride de geçen kalemler.
 */
export function ImportDialog({ opened, catalog, onClose, onImported }: ImportDialogProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`materials.import.${key}`, options);
  const errorText = useErrorText();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<CatalogImportReport | null>(null);

  useEffect(() => {
    if (!opened) return;
    setFile(null);
    setError(null);
    setReport(null);
  }, [opened]);

  const start = async () => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError('FILE_TOO_LARGE');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await catalog.importFile(file);
      setReport(result);
      onImported(result);
    } catch (failure) {
      setError(errorCode(failure));
    } finally {
      setBusy(false);
    }
  };
  const issue = (code: string) => t(`materials.import.codes.${code}`, { defaultValue: code });
  const field = (name: string) => t(`materials.import.fields.${name}`, { defaultValue: name });

  return (
    <Modal
      opened={opened}
      onClose={busy ? () => {} : onClose}
      title={k('title')}
      closeButtonProps={{ 'aria-label': k('close') }}
      size="xl"
    >
      <Stack gap="md">
        {report ? (
          <>
            <Alert color="green" icon={<IconCircleCheck size={18} />}>
              {k('done', { rows: report.rows })}
            </Alert>
            <section aria-label={k('summary')}>
              <dl className={classes.reportCounts}>
                {(
                  [
                    ['added', report.added],
                    ['updated', report.updated],
                    ['unchanged', report.unchanged],
                    ['merged', report.merged],
                    ['skipped', report.skipped],
                    ['categoriesAdded', report.categoriesAdded],
                  ] as const
                ).map(([key, value]) => (
                  <div key={key}>
                    <dt>{k(`counts.${key}`)}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            </section>
            {[...report.errors, ...report.warnings].length > 0 ? (
              <section aria-label={k('issues')}>
                <h4 className={classes.reportTitle}>{k('issues')}</h4>
                <div className={classes.reportTable}>
                  <table className={classes.miniTable}>
                    <thead>
                      <tr>
                        <th scope="col">{k('row')}</th>
                        <th scope="col">{k('field')}</th>
                        <th scope="col">{k('problem')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...report.errors, ...report.warnings]
                        .sort((a, b) => a.row - b.row)
                        .map((entry) => (
                          <tr key={`${entry.row}-${entry.field}-${entry.code}`}>
                            <td className={classes.number}>{entry.row}</td>
                            <td>{field(entry.field)}</td>
                            <td>{issue(entry.code)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}
            {report.crossCategory.length > 0 ? (
              <section aria-label={k('crossTitle')}>
                <h4 className={classes.reportTitle}>{k('crossTitle')}</h4>
                <p className={classes.muted}>{k('crossHint')}</p>
                <div className={classes.reportTable}>
                  <table className={classes.miniTable}>
                    <thead>
                      <tr>
                        <th scope="col">{k('item')}</th>
                        <th scope="col">{k('categories')}</th>
                        <th scope="col">{k('rows')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.crossCategory.map((entry) => (
                        <tr key={entry.rows.join('-')}>
                          <td>
                            {entry.name}
                            {entry.specification ? ` · ${entry.specification}` : ''}
                          </td>
                          <td>{entry.categories.map((c) => c.mainCategory).join(' / ')}</td>
                          <td className={classes.number}>{entry.rows.join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}
            <Group justify="flex-end">
              <Button onClick={onClose}>{k('close')}</Button>
            </Group>
          </>
        ) : (
          <>
            <p className={classes.muted}>{k('hint')}</p>
            {error ? (
              <Alert color="red" icon={<IconAlertCircle size={18} />} role="alert">
                {errorText(error)}
              </Alert>
            ) : null}
            <FileInput
              label={k('file')}
              placeholder={k('choose')}
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              leftSection={<IconFileSpreadsheet size={16} />}
              value={file}
              onChange={setFile}
              clearable
              disabled={busy}
            />
            {busy ? (
              <Group gap="sm" role="status">
                <Loader size="sm" />
                <span className={classes.muted}>{k('working')}</span>
              </Group>
            ) : null}
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={onClose} disabled={busy}>
                {k('cancel')}
              </Button>
              <Button onClick={() => void start()} disabled={!file} loading={busy}>
                {k('start')}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
